
import VariableTree from './VariableTree';
import Select from 'react-select';
import React, { useState, useRef, useEffect } from 'react';
import useAPI from '../../../../hooks/useAPI';
import { VscVariableGroup } from 'react-icons/vsc';
import Picker from 'emoji-picker-react';
import styledComponent from 'styled-components';
import NodeModal from './NodeModal';
import CustomMenuList from './CustomMenuList';
import { PiSticker } from "react-icons/pi";
import { isStickerUrl } from './stickerMessage';
import { upload_s3 } from '../../../content/UploaderS3';
import { questionToast } from '../../../plugins/toastAll';
import { NODE_THEMES } from './NodeTheme';
import { useQuery } from '@tanstack/react-query';

const typeColors = {
  queryApi: '#7ECCD8',
  default: '#A7ADA7',
  webHook: '#FC6530',
};
const getFieldConfig = (type, name, theme) => {
  const isTextArea = type === 'textArea';

  return {
    containerClass: 'col p-0',
    fieldStyle: isTextArea
      ? {
          width: '100%',
          minHeight: name == 'user_response' ? '60px' : '90px',
          height: name == 'user_response' ? '60px' : '90px',
          maxHeight: '400px',
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          padding: '10px',
          resize: 'vertical',
        }
      : {
          width: '100%',
          height: '42px',
          borderRadius: '8px',
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          padding: '2px 10px',
          lineHeight: '28px',
        },
    fieldClass: isTextArea ? 'form-control' : 'form-control custom-textarea',
    labelStyle: {
      color: theme.textSecondary,
      fontSize: '11px',
      fontWeight: 600,
      marginBottom: '5px',
    },
  };
};

const SUTextField = ({
  id,
  name,
  label,
  placeholder = '',
  hint = null,
  maxLength = null,
  getValues,
  setValue,
  errors,
  selectedNode,
  contextVariables = [],
  type = 'text',
  showVariables = false,
  showEmoji = false,
  showSticker = false,
  typeVariables = ['all'],
  readOnly = false,
  allowCopy = false,
}) => {
  const { urlAPI_1, urlAPI_2, urlAPI_3, fetchWithAuth } = useAPI();

  const fieldRef = useRef(null);
  const cursorRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const [showPicker, setShowPicker] = useState(false);
  const [variablesModal, setVariablesModal] = useState(false);
  // Tema en vivo publicado por el Header (ver BotsConstructor).
  const { data: isDarkTheme } = useQuery({
    queryKey: ['isDarkTheme'],
    queryFn: () => localStorage.getItem('IS_DARK') === 'true',
    staleTime: Infinity,
  });
  const isDark = isDarkTheme ? 'true' : 'false';
  const [isFieldEmpty, setIsFieldEmpty] = useState(true);
  const [charCount, setCharCount] = useState(0);
  // Sticker state
  const [stickerFile, setStickerFile] = useState(null);
  const [stickerUrl, setStickerUrl] = useState('');
  const [stickerLoading, setStickerLoading] = useState(false);
  // Sticker handler
  const handleStickerChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 102400) {
      await questionToast({
        title: 'Sticker demasiado grande',
        text: 'El sticker no puede superar los 100kb.',
        icon: 'warning',
        buttons: { success: 'Aceptar' }
      }, () => {});
      return;
    }
    let webpFile = file;
    if (file.type === 'image/png' || file.type === 'image/webp') {
      const img = new window.Image();
      img.onload = async () => {
        const isExactSize = img.width === 512 && img.height === 512;
        if (isExactSize) {
          if (file.type === 'image/webp') {
            await uploadStickerToS3(file);
            return;
          } else if (file.type === 'image/png') {
            const canvas = document.createElement('canvas');
            canvas.width = 512;
            canvas.height = 512;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, 512, 512);
            ctx.drawImage(img, 0, 0, 512, 512);
            canvas.toBlob(async (blob) => {
              if (blob) {
                webpFile = new File([blob], file.name.replace(/\.(png|webp)$/i, '.webp'), { type: 'image/webp' });
                await uploadStickerToS3(webpFile);
              } else {
                alert('No se pudo convertir la imagen a WebP.');
              }
            }, 'image/webp');
            return;
          }
        }
        const data = {
          title: 'Reajustar sticker',
          text: 'El sticker no es 512x512 px. ¿Quieres que el sistema lo reajuste automáticamente? Si eliges "Cancelar", deberás subir una imagen de 512x512 px.',
          icon: 'question',
          buttons: { success: 'Aceptar', cancel: 'Cancelar' }
        };
        const confirmResize = await questionToast(data, () => {});
        if (!confirmResize || confirmResize.isConfirmed === false) {
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, 512, 512);
        const scale = Math.min(512 / img.width, 512 / img.height);
        const newWidth = img.width * scale;
        const newHeight = img.height * scale;
        const x = (512 - newWidth) / 2;
        const y = (512 - newHeight) / 2;
        ctx.drawImage(img, x, y, newWidth, newHeight);
        canvas.toBlob(async (blob) => {
          if (blob) {
            webpFile = new File([blob], file.name.replace(/\.(png|webp)$/i, '.webp'), { type: 'image/webp' });
            await uploadStickerToS3(webpFile);
          } else {
            alert('No se pudo convertir la imagen a WebP.');
          }
        }, 'image/webp');
      };
      img.onerror = () => {
        alert('No se pudo cargar la imagen.');
      };
      img.src = URL.createObjectURL(file);
      return;
    } else {
      alert('Solo se permiten stickers en formato .webp o .png');
    }
  };

  const uploadStickerToS3 = async (file) => {
    setStickerLoading(true);
    setStickerFile(file);
    try {
      // Get signed URL
      const key_name = encodeURIComponent(file.name);
      const url = urlAPI_1 + `getSignedURL/${key_name}/${file.size}`;
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);
      if (response.status === 200) {
        let signedUrl = await response.json();
        if (signedUrl && !signedUrl.error) {
          const s3url = await upload_s3(file, signedUrl);
          if (s3url) {
            setStickerUrl(s3url);
            setValue(name, s3url, { shouldDirty: true });
          } else {
            alert('Error subiendo el sticker a S3.');
            setStickerFile(null);
            setStickerUrl('');
            setValue(name, '', { shouldDirty: true });
          }
        } else {
          alert('Error obteniendo la URL firmada para S3.');
        }
      } else {
        alert('Error obteniendo la URL firmada para S3.');
      }
    } catch (err) {
      alert('Error subiendo el sticker a S3.');
      setStickerFile(null);
      setStickerUrl('');
      setValue(name, '', { shouldDirty: true });
    } finally {
      setStickerLoading(false);
    }
  };

  const handleRemoveSticker = () => {
    setStickerFile(null);
    setStickerUrl('');
    setValue(name, '', { shouldDirty: true });
  };

  const [correspondence, setCorrespondence] = useState([]);
  const [options, setOptions] = useState([]);

  // #region Handle Field.
  // Handle Insert
  const createChip = (raw) => {
    const full = raw.slice(2, -2);
    let visible = full;
    let color = typeColors.default;

    const matchType = full.match(/([^.{}-]+)[-.](queryApi|webHook)\.(.+)$/);
    if (matchType) {
      const [, nodeId, type, field] = matchType;
      visible = field;
      color = typeColors[type] || typeColors.default;
    }

    const span = document.createElement('span');
    span.textContent = visible;
    span.setAttribute('data-value', raw);
    span.contentEditable = 'false';
    span.className = 'chip';
    span.style.backgroundColor = color;

    span.addEventListener('dblclick', () => {
      const match = full.match(/^([^.]+)\./);
      const nodeId = match?.[1];

      if (nodeId) {
        const nodeElement = document.querySelector(`[data-id="${nodeId}"]`);
        if (nodeElement) {
          nodeElement.classList.remove('hover-pulse');
        }
      }

      const editableSpan = document.createElement('span');
      editableSpan.contentEditable = 'true';
      editableSpan.textContent = raw;
      editableSpan.className = 'editing-chip';

      span.replaceWith(editableSpan);
      placeCursorAtEnd(editableSpan);
      editableSpan.focus();

      const restoreIfValid = () => {
        const value = editableSpan.textContent?.trim() || '';
        const isValid = /^{{[^{}]+}}$/.test(value);
        if (isValid) {
          const newChip = createChip(value);
          editableSpan.replaceWith(newChip);
        }
      };

      editableSpan.addEventListener('focusout', () => {
        setTimeout(() => {
          if (
            !document.activeElement ||
            !editableSpan.contains(document.activeElement)
          ) {
            restoreIfValid();
          }
        }, 0);
      });
    });

    span.addEventListener('mouseenter', () => {
      const full = raw.slice(2, -2);
      const match = full.match(/^([^.]+)\./);

      if (match) {
        const nodeId = match[1];
        const nodeElement = document.querySelector(`[data-id="${nodeId}"]`);
        if (nodeElement) {
          nodeElement.classList.add('hover-pulse');
        }
      }
    });

    span.addEventListener('mouseleave', () => {
      const full = raw.slice(2, -2);
      const match = full.match(/^([^.]+)\./);

      if (match) {
        const nodeId = match[1];
        const nodeElement = document.querySelector(`[data-id="${nodeId}"]`);
        if (nodeElement) {
          nodeElement.classList.remove('hover-pulse');
        }
      }
    });

    return span;
  };
  const getTextFromEditor = () => {
    if (!fieldRef.current) return '';

    let result = '';
    fieldRef.current.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        result += node.textContent;
      } else if (
        node.nodeType === Node.ELEMENT_NODE &&
        node.classList.contains('chip')
      ) {
        result += node.getAttribute('data-value') || node.textContent;
      }
    });

    return result.replace(/\u200B/g, '').trim();
  };

  const handleKeyUp = (e) => {
    if (e.key !== ' ' && e.key !== 'Enter') return;

    const selection = window.getSelection();
    if (!selection || !selection.anchorNode || !fieldRef.current) return;

    const node = selection.anchorNode;
    const text = node.textContent || '';

    const match = text.match(/{{[^{}]+}}/g);
    if (!match) return;

    const lastMatch = match[match.length - 1];
    const index = text.lastIndexOf(lastMatch);
    if (index === -1) return;

    const range = document.createRange();
    range.setStart(node, index);
    range.setEnd(node, index + lastMatch.length);

    const raw = lastMatch;

    const chip = createChip(raw);
    range.deleteContents();
    range.insertNode(chip);

    const space = document.createTextNode('\u00A0');
    chip.after(space);
    const newRange = document.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);
  };
  const handleKeyDown = (e) => {
    if (e.key !== 'Backspace') return;

    const selection = window.getSelection();
    if (!selection || !selection.anchorNode) return;

    const node = selection.anchorNode;
    const offset = selection.anchorOffset;

    if (node.nodeType === Node.TEXT_NODE && offset === 0) {
      const prev = node.previousSibling;
      if (prev && prev.classList?.contains('chip')) {
        e.preventDefault();
        prev.remove();
      }
    }

    if (node.nodeType === Node.ELEMENT_NODE && offset > 0) {
      const child = node.childNodes[offset - 1];
      if (child && child.classList?.contains('chip')) {
        e.preventDefault();
        child.remove();
      }
    }
  };
  const renderTextWithChips = (text) => {
    if (!fieldRef.current) return;
    fieldRef.current.innerHTML = '';

    const parts = text?.split(/({{[^{}]+}})/g);
    parts?.forEach((part) => {
      if (/^{{[^{}]+}}$/.test(part)) {
        const chip = createChip(part);
        fieldRef.current.appendChild(chip);
      } else {
        fieldRef.current.appendChild(document.createTextNode(part));
      }
    });

    const last = fieldRef.current.lastChild;
    if (!last || last.nodeType !== Node.TEXT_NODE || last.textContent === '') {
      const spacer = document.createTextNode('\u200B');
      fieldRef.current.appendChild(spacer);
    }
  };
  const fixCursorIfNeeded = () => {
    const sel = window.getSelection();
    if (!sel || !fieldRef.current.contains(sel.anchorNode)) {
      const range = document.createRange();
      range.selectNodeContents(fieldRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };
  // Handle Cursor
  const placeCursorAtEnd = (el) => {
    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(el);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  };
  const saveCursor = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      cursorRef.current = sel.getRangeAt(0).cloneRange();
    }
  };
  const restoreCursor = () => {
    const sel = window.getSelection();
    sel.removeAllRanges();
    if (cursorRef.current) {
      sel.addRange(cursorRef.current);
    }
  };
  // #endregion

  // #region Insert Emojis.
  const onVariableClick = (variable) => {
    // Si el campo no tiene foco o selección, enfocar y poner cursor al final
    const sel = window.getSelection();
    const field = fieldRef.current;
    if (!field) return;
    if (!sel || !field.contains(sel.anchorNode)) {
      field.focus();
      // Coloca el cursor al final
      const range = document.createRange();
      range.selectNodeContents(field);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
      // Guarda el cursor al final
      saveCursor();
    } else {
      restoreCursor();
    }
    insertVariableAtCursor(variable);
  };
  const onEmojiClick = (event, emojiObject) => {
    const sel = window.getSelection();
    const field = fieldRef.current;
    if (!field) return;
    if (!sel || !field.contains(sel.anchorNode)) {
      field.focus();
      // Coloca el cursor al final
      const range = document.createRange();
      range.selectNodeContents(field);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
      saveCursor();
    } else {
      restoreCursor();
    }
    insertEmojiAtCursor(emojiObject.emoji);
    setTimeout(() => {
      fieldRef.current?.focus();
    }, 0);
  };
  const insertEmojiAtCursor = (emoji) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    range.deleteContents();

    const emojiNode = document.createTextNode(emoji);
    range.insertNode(emojiNode);

    range.setStartAfter(emojiNode);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);

    const updatedText = getTextFromEditor();
    setValue(name, updatedText, { shouldDirty: true });
    setCharCount(updatedText.length);
  };
  // #endregion

  // #region Insert Variables.
  const insertVariableAtCursor = (variable) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    const range = sel.getRangeAt(0);
    range.deleteContents();

    const chip = createChip(variable);
    range.insertNode(chip);

    const space = document.createTextNode('\u00A0');
    chip.after(space);

    const newRange = document.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    const updatedText = getTextFromEditor();
    setValue(name, updatedText, { shouldDirty: true });
    setCharCount(updatedText.length);

    setTimeout(() => {
      fieldRef.current?.focus();
    }, 0);
  };
  const handleClickModalVariables = (event) => {
    setAnchorEl(event.currentTarget);
    setVariablesModal(true);
    getOptions();
  };
  const getOptions = () => {
    const showAll = typeVariables.includes('all');
    const showGeneral = showAll || typeVariables.includes('general');
    const showApi = showAll || typeVariables.includes('api');

    let generalOptions = correspondence.map((item) => ({
      label: item.fieldName,
      value: item.fieldCorresp,
    }));

    if ((name || id || '').includes('rule_value1')) {
      generalOptions = [
        { label: 'Respuesta nodo', value: '{{response_node}}' },
        { label: 'Respuesta API', value: '{{response_api}}' },
        { label: 'Respuesta WebHook', value: '{{response_webhook}}' },
        ...generalOptions,
      ];
    }

    const combinedOptions = [
      ...(showGeneral
        ? [
            {
              label: 'Variables generales',
              id: 'general',
              options: generalOptions,
            },
          ]
        : []),

      ...(showApi
        ? contextVariables?.map((context) => ({
            label: context.api,
            id: context.id,
            options: context.data,
          })) || []
        : []),
    ];
    setOptions(combinedOptions);
  };
  // #endregion

  // #region Others Resources.
  const getCorresp = async () => {
    try {
      const url = urlAPI_2 + 'getDataEmail';
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);
      const result = await response.json();
      let data = result.data;
      setCorrespondence(data);
      if (result.message === 'Tu petición no tiene cabecera de autorización') {
        navigate('/');
      }
    } catch (error) {
      console.error(error);
    }
  };
  // #endregion

  const getCustomStyles = (isDark) => ({
    menuList: (base) => ({
      ...base,
      backgroundColor: isDark === 'true' ? '#202c33' : '#fff',
      scrollbarWidth: 'thin',
      padding: 0,
      borderRadius: 0,
      minWidth: 260,
      maxHeight: 350,
      height: '100%',
    }),
    groupHeading: (base) => ({
      ...base,
      backgroundColor: '#cfd8dc',
      color: '#263238',
      fontWeight: 'bold',
      fontSize: '13px',
      padding: '6px 10px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      borderBottom: '1px solid #b0bec5',
    }),
    option: (provided, state) => {
      const isSelected = state.isSelected;
      const isFocused = state.isFocused;

      return {
        ...provided,
        backgroundColor:
          isDark === 'true'
            ? isSelected
              ? '#455a64'
              : isFocused
              ? '#374954'
              : '#202c33'
            : isSelected
            ? 'lightblue'
            : isFocused
            ? 'lightgray'
            : '#fff',
        color: isDark === 'true' ? 'white' : '#333',
        cursor: 'pointer',
      };
    },
    menu: (base) => ({
      ...base,
      backgroundColor: isDark === 'true' ? '#202c33' : '#fff',
      borderRadius: 10,
      boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
      border: isDark === 'true' ? '1px solid #263238' : '1px solid #b0bec5',
      zIndex: 1300,
      minWidth: 260,
      maxHeight: 350,
      overflow: 'hidden',
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 1300,
    }),
    input: (base) => ({
      ...base,
      color: isDark === 'true' ? 'white' : '#333',
    }),
  });

  // #region Filed Style.
  const theme = isDark == 'true' ? NODE_THEMES.dark : NODE_THEMES.light;
  const { containerClass, fieldStyle, fieldClass, labelStyle } = getFieldConfig(
    type,
    name,
    theme
  );
  // #endregion

  useEffect(() => {
    setTimeout(() => {
      const value = getValues(name);
      renderTextWithChips(value);
      setIsFieldEmpty(!value || String(value).trim() === '');
      setCharCount(String(value ?? '').length);
      if (showSticker) {
        setStickerFile(null);
        setStickerUrl(isStickerUrl(value) ? String(value).trim() : '');
      }
    }, 0);
  }, [selectedNode, showSticker]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([getCorresp()]);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  return (
    <div className={containerClass}>
      {label && (
        <label htmlFor={id} style={labelStyle}>
          {label}
        </label>
      )}
      <div>
        <div style={{ position: 'relative', width: '100%' }}>
          {showSticker && (stickerFile || stickerUrl) ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: 'transparent',
              borderRadius: '8px',
              padding: '10px',
              minHeight: 90,
              justifyContent: 'space-between',
            }}>
              {stickerLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ display: 'inline-block', width: 32, height: 32, border: '4px solid #7ECCD8', borderTop: '4px solid #e0e0e0', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: 4 }} />
                  <span style={{ color: theme.textSecondary, fontWeight: 'bold' }}>Subiendo sticker...</span>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </div>
              ) : (
                <img
                  src={stickerUrl || (stickerFile && URL.createObjectURL(stickerFile))}
                  alt="Sticker preview"
                  style={{ maxHeight: 64, maxWidth: 64, borderRadius: 8 }}
                />
              )}
              <button
                type="button"
                onClick={handleRemoveSticker}
                style={{ background: 'none', border: 'none', color: '#E5253D', fontWeight: 'bold', cursor: 'pointer', fontSize: 18 }}
                title="Eliminar sticker"
                disabled={stickerLoading}
              >
                ✕
              </button>
            </div>
          ) : (
            <>
              <div
                ref={fieldRef}
                id={id}
                name={name}
                className={fieldClass}
                style={{
                  ...fieldStyle,
                  backgroundColor: readOnly
                    ? theme.inputBg
                    : fieldStyle.backgroundColor,
                  cursor: allowCopy
                    ? 'pointer'
                    : readOnly
                    ? 'default'
                    : fieldStyle.cursor,
                  minHeight: fieldStyle.height,
                  userSelect: 'text',
                  opacity: showSticker && stickerFile ? 0.5 : 1,
                }}
                onKeyUp={readOnly || allowCopy || (showSticker && stickerFile) ? undefined : handleKeyUp}
                onKeyDown={readOnly || allowCopy || (showSticker && stickerFile) ? undefined : handleKeyDown}
                contentEditable={!readOnly && !allowCopy && !(showSticker && stickerFile)}
                suppressContentEditableWarning
                aria-readonly={readOnly || allowCopy || (showSticker && stickerFile)}
                tabIndex={0}
                onPaste={(e) => {
                  if (allowCopy) return;
                  e.preventDefault();
                  const text = e.clipboardData.getData('text/plain');
                  document.execCommand('insertText', false, text);
                }}
                onInput={
                  readOnly || allowCopy || (showSticker && stickerFile)
                    ? undefined
                    : () => {
                        const currentText = getTextFromEditor();
                        setValue(name, currentText, { shouldDirty: true });
                        setIsFieldEmpty(!currentText || currentText.trim() === '');
                        setCharCount(currentText.length);
                      }
                }
                onMouseEnter={allowCopy ? () => setShowTooltip(true) : undefined}
                onMouseLeave={allowCopy ? () => setShowTooltip(false) : undefined}
              />
              {placeholder &&
                isFieldEmpty &&
                !readOnly &&
                !(showSticker && stickerFile) && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: type === 'textArea' ? '10px' : '9px',
                      color: theme.textTertiary,
                      pointerEvents: 'none',
                      fontSize: '13px',
                    }}
                  >
                    {placeholder}
                  </div>
                )}
            </>
          )}
          {allowCopy && (showTooltip || copied) && (
            <div
              style={{
                position: 'absolute',
                top: '-32px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#222',
                color: '#fff',
                padding: '4px 12px',
                borderRadius: 6,
                fontSize: 13,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                opacity: copied ? 1 : 0.85,
                transition: 'opacity 0.2s',
                zIndex: 10,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            >
              {copied ? '¡Copiado!' : 'Copiar'}
            </div>
          )}
        </div>

        {errors?.[name] && <Alert>* {errors[name].message}</Alert>}

        {(hint || maxLength) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              margin: '4px 0 0 0',
            }}
          >
            {hint && (
              <p
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 11,
                  color: theme.textTertiary,
                  fontWeight: 500,
                  margin: 0,
                }}
              >
                {hint}
              </p>
            )}
            {maxLength && (
              <span
                style={{
                  flexShrink: 0,
                  marginLeft: hint ? 0 : 'auto',
                  fontSize: 10,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  color:
                    charCount > maxLength
                      ? '#F87171'
                      : charCount > maxLength * 0.9
                      ? '#F59E0B'
                      : theme.textTertiary,
                }}
              >
                {charCount} / {maxLength}
              </span>
            )}
          </div>
        )}

        <div className="d-flex align-items-center">
          {showVariables && (
            <div className="pt-2">
              <button
                className="border-0 bg-transparent d-block pointer"
                type="button"
                style={{ color: theme.textSecondary }}
                onClick={(event) => handleClickModalVariables(event)}
              >
                <VscVariableGroup style={{ fontSize: '18px' }} />
              </button>
            </div>
          )} 

          {showSticker && (
            <div className="pt-2">
              <button
                className="border-0 bg-transparent d-block pointer"
                type="button"
                title="Subir sticker (.webp)"
                onClick={() => document.getElementById(`${id}-sticker-input`).click()}
                style={{ padding: 0, margin: 0, display: 'flex', alignItems: 'center' }}
              >
                <PiSticker style={{ fontSize: 24, color: theme.textSecondary }} />
              </button>
                  <input
                    id={`${id}-sticker-input`}
                    type="file"
                    accept="image/webp,image/png"
                    style={{ display: 'none' }}
                    onChange={handleStickerChange}
                    disabled={!!stickerFile}
                  />
            </div>
          )}

          {showEmoji && (
            <div className="picker-container pt-4" style={{ width: '100%' }}>
              <i
                className={
                  showPicker
                    ? 'icon-smile emoji-icon active'
                    : 'icon-smile emoji-icon'
                }
                style={{ fontSize: '22px' }}
                onClick={() => setShowPicker((val) => !val)}
              />
              {showPicker && (
                <Picker
                  pickerStyle={{
                    width: '100%',
                    top: '30px',
                    position: 'absolute',
                    zIndex: '999',
                  }}
                  onEmojiClick={onEmojiClick}
                  groupNames={{
                    smileys_people: 'Emoticones y personas',
                    animals_nature: 'Animales y naturaleza',
                    food_drink: 'Alimentos y bebidas',
                    travel_places: 'Viajes y lugares',
                    activities: 'Actividades',
                    objects: 'Objetos',
                    symbols: 'Símbolos',
                    flags: 'Banderas',
                    recently_used: 'Recientes',
                  }}
                />
              )}
            </div>
          )}
        </div>

        {/* DIALOG VARIABLES */}
        <NodeModal
          open={variablesModal}
          onClose={() => setVariablesModal(false)}
          anchorEl={anchorEl}
          width={360}
          isDark={isDark}
          icon={VscVariableGroup}
          color="#5BC0DE"
          eyebrow="Insertar"
          title="Variables disponibles"
        >
          <div className="su-control-group">
            {/* Variables generales como select clásico */}
            {(() => {
              const generalGroup = options.find((g) => g.id === 'general');
              if (!generalGroup) return null;
              return (
                <Select
                  styles={getCustomStyles(isDark)}
                  className="form-control basic-multi-select"
                  placeholder="Buscar..."
                  name="variables"
                  onChange={(val) => onVariableClick(val.value)}
                  options={generalGroup.options}
                  classNamePrefix="select"
                  noOptionsMessage={() => 'Sin opciones'}
                  filterOption={(option, inputValue) =>
                    typeof option.label === 'string' &&
                    option.label
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  getOptionValue={(option) => option.value}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  menuShouldScrollIntoView={false}
                  isSearchable={true}
                />
              );
            })()}

            {/* Para cada grupo de API, un select que al abrir muestra VariableTree de ese grupo */}
            {options
              .filter((g) => g.id !== 'general')
              .map((group) => (
                <div key={group.id} style={{ marginTop: 18 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      color: isDark === 'true' ? '#fff' : '#263238',
                      marginBottom: 4,
                    }}
                  >
                    {group.label}
                  </div>
                  <Select
                    styles={getCustomStyles(isDark)}
                    className="form-control basic-multi-select"
                    placeholder={`Buscar variable de ${group.label}...`}
                    name={`variablesApi_${group.id}`}
                    options={group.options}
                    classNamePrefix="select"
                    noOptionsMessage={() => 'Sin opciones'}
                    filterOption={(option, inputValue) =>
                      typeof option.label === 'string' &&
                      option.label
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    }
                    getOptionValue={(option) => option.value}
                    menuPortalTarget={document.body}
                    menuPosition="absolute"
                    menuPlacement="auto"
                    menuShouldScrollIntoView={true}
                    maxMenuHeight={350}
                    isSearchable={true}
                    components={{
                      MenuList: (props) => (
                        <div
                          style={{
                            height: '100%',
                            maxHeight: 350,
                            minWidth: 260,
                            overflowY: 'auto',
                            background: 'transparent',
                            padding: 0,
                            borderRadius: 0,
                          }}
                        >
                          <VariableTree
                            data={group.options}
                            onSelect={onVariableClick}
                            isDark={isDark}
                          />
                        </div>
                      ),
                    }}
                    onChange={(val) => onVariableClick(val.value)}
                  />
                </div>
              ))}
          </div>
        </NodeModal>
      </div>
    </div>
  );
};

export default SUTextField;

const Alert = styledComponent.span`
  color: red;
  display: block;
  width: 100%;
`;
